import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, fade } from "@material-ui/core/styles";
import { Button, Checkbox, CircularProgress, FormControlLabel, IconButton, Tooltip } from "@material-ui/core";
import EventRoundedIcon from "@material-ui/icons/EventRounded";
import ChevronLeftRoundedIcon from "@material-ui/icons/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@material-ui/icons/ChevronRightRounded";
import RemoveRoundedIcon from "@material-ui/icons/RemoveRounded";
import ArrowBackRoundedIcon from "@material-ui/icons/ArrowBackRounded";
import { toast } from "react-toastify";

import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const VERDE = "#0d9488";
const AZUL = "#2563eb";

const useStyles = makeStyles(theme => {
  const escuro = theme.palette.type === "dark";
  return {
    caixa: {
      position: "fixed", right: 24, bottom: 24, zIndex: 1400, width: 380, maxWidth: "calc(100vw - 32px)",
      borderRadius: 20, overflow: "hidden", background: theme.palette.background.paper,
      boxShadow: "0 24px 60px rgba(15,23,42,.28)", border: `2px solid ${fade(AZUL, .35)}`,
      animation: "$entrar .35s ease, $pulsar 2.4s ease-in-out 1s infinite",
    },
    "@keyframes entrar": { from: { opacity: 0, transform: "translateY(20px)" }, to: { opacity: 1, transform: "none" } },
    "@keyframes pulsar": {
      "0%,100%": { boxShadow: `0 24px 60px rgba(15,23,42,.28), 0 0 0 0 ${fade(AZUL, .35)}` },
      "50%": { boxShadow: `0 24px 60px rgba(15,23,42,.28), 0 0 0 8px ${fade(AZUL, 0)}` },
    },
    topo: {
      display: "flex", alignItems: "center", gap: 10, padding: "12px 12px 12px 16px", color: "#fff",
      background: `linear-gradient(135deg, ${AZUL}, #4f46e5)`,
    },
    corpo: { padding: 18, display: "flex", flexDirection: "column", gap: 12 },
    nome: { fontWeight: 800, fontSize: 18, lineHeight: 1.25 },
    linha: { fontSize: 13.5, opacity: .8 },
    tempo: { display: "inline-block", fontSize: 12.5, fontWeight: 700, padding: "4px 10px", borderRadius: 999 },
    botoes: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
    botao: { borderRadius: 12, textTransform: "none", fontWeight: 700, padding: "10px 12px" },
    veio: { background: "#16a34a", color: "#fff", "&:hover": { background: "#15803d" } },
    faltou: { background: "#dc2626", color: "#fff", "&:hover": { background: "#b91c1c" } },
    reagendar: { gridColumn: "1 / -1", border: `1px solid ${fade(AZUL, .4)}`, color: AZUL },
    link: { fontSize: 13, textTransform: "none", alignSelf: "flex-start", padding: "2px 6px" },
    dias: { display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4 },
    chip: {
      flexShrink: 0, border: `1px solid ${theme.palette.divider}`, background: "transparent", color: "inherit",
      font: "inherit", fontSize: 12.5, fontWeight: 600, padding: "8px 12px", borderRadius: 12, cursor: "pointer",
      textAlign: "center", lineHeight: 1.25,
    },
    chipAtivo: { background: VERDE, color: "#fff", borderColor: VERDE },
    horas: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6, maxHeight: 150, overflowY: "auto" },
    pilula: {
      position: "fixed", right: 24, bottom: 24, zIndex: 1400, display: "flex", alignItems: "center", gap: 8,
      padding: "12px 18px", borderRadius: 999, cursor: "pointer", color: "#fff", fontWeight: 700, fontSize: 14,
      background: `linear-gradient(135deg, ${AZUL}, #4f46e5)`, boxShadow: "0 12px 30px rgba(37,99,235,.4)",
      animation: "$pulsar 2.4s ease-in-out infinite", border: 0,
    },
    vazio: { fontSize: 13, opacity: .65, padding: "8px 0" },
  };
});

const hora = d => new Date(d).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
const diaCurto = d => new Date(d).toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" }).replace(".", "");
const chaveDia = d => { const x = new Date(d); return `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`; };

/**
 * Aviso fixo na tela do RH (operador): a entrevista chegou e ainda nao tem
 * resultado. So sai quando marca Compareceu, Faltou ou reagenda.
 */
const AvisoEntrevista = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const ativo = user?.profile === "operador";

  const [lista, setLista] = useState([]);
  const [indice, setIndice] = useState(0);
  const [minimizado, setMinimizado] = useState(false);
  const [modo, setModo] = useState("aviso");
  const [salvando, setSalvando] = useState(false);
  const [livres, setLivres] = useState(null);
  const [dia, setDia] = useState("");
  const [novoId, setNovoId] = useState(null);
  const [avisar, setAvisar] = useState(true);
  const [, setTick] = useState(0);

  const carregar = useCallback(async () => {
    if (!ativo) return;
    try {
      const { data } = await api.get("/interviews/pendentes");
      setLista(Array.isArray(data) ? data : []);
    } catch (e) {
      /* tenta de novo no proximo ciclo */
    }
  }, [ativo]);

  useEffect(() => {
    if (!ativo) return undefined;
    carregar();
    const t = setInterval(() => { carregar(); setTick(x => x + 1); }, 60000);
    const socket = openSocket();
    socket.on("interviewSlot", carregar);
    return () => { clearInterval(t); socket.off("interviewSlot", carregar); };
  }, [ativo, carregar]);

  const atual = lista[Math.min(indice, Math.max(0, lista.length - 1))];
  useEffect(() => { setModo("aviso"); setNovoId(null); setDia(""); }, [atual?.id]);
  // chegou entrevista nova: aparece aberto
  useEffect(() => { if (lista.length) setMinimizado(false); }, [lista.length]);

  const porDia = useMemo(() => {
    const m = new Map();
    (livres || []).forEach(s => {
      const k = chaveDia(s.startAt);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(s);
    });
    return m;
  }, [livres]);

  if (!ativo || !atual) return null;

  if (minimizado) {
    return (
      <button type="button" className={classes.pilula} onClick={() => setMinimizado(false)}>
        <EventRoundedIcon /> {lista.length} entrevista{lista.length > 1 ? "s" : ""} aguardando resultado
      </button>
    );
  }

  const minutos = Math.round((new Date(atual.startAt).getTime() - Date.now()) / 60000);
  const textoTempo = minutos > 0 ? `Começa em ${minutos} min` : minutos === 0 ? "Começa agora" :
    -minutos < 60 ? `Começou há ${-minutos} min` : `Foi ${new Date(atual.startAt).toLocaleDateString("pt-BR")} às ${hora(atual.startAt)}`;
  const corTempo = minutos > 0 ? "#2563eb" : -minutos > 60 ? "#dc2626" : "#d97706";

  const marcar = async outcome => {
    setSalvando(true);
    try {
      await api.put(`/interviews/${atual.id}`, { outcome });
      toast.success(outcome === "compareceu" ? "Marcado: compareceu ✅" : "Marcado: faltou ❌");
      setLista(l => l.filter(x => x.id !== atual.id));
      setIndice(0);
    } catch (e) { toastError(e); }
    setSalvando(false);
  };

  const abrirReagendar = async () => {
    setModo("reagendar");
    setLivres(null);
    try {
      const { data } = await api.get("/interviews/livres");
      const futuros = (data || []).filter(s => new Date(s.startAt).getTime() > Date.now());
      setLivres(futuros);
      if (futuros.length) setDia(chaveDia(futuros[0].startAt));
    } catch (e) { toastError(e); setLivres([]); }
  };

  const reagendar = async () => {
    if (!novoId) return toast.info("Escolha o novo horário.");
    setSalvando(true);
    try {
      const { data } = await api.put(`/interviews/${atual.id}`, { action: "reschedule", novoSlotId: novoId, notify: avisar });
      toast.success(`Reagendada para ${diaCurto(data.novo.startAt)} às ${hora(data.novo.startAt)}${data.notified ? " — candidato avisado" : ""}`);
      setLista(l => l.filter(x => x.id !== atual.id));
      setIndice(0);
    } catch (e) { toastError(e); }
    setSalvando(false);
  };

  const doDia = porDia.get(dia) || [];

  return (
    <div className={classes.caixa}>
      <div className={classes.topo}>
        <EventRoundedIcon />
        <div style={{ flex: 1, fontWeight: 800, fontSize: 14.5 }}>
          {modo === "reagendar" ? "Reagendar entrevista" : "Entrevista agora"}
          {lista.length > 1 && <span style={{ opacity: .8, fontWeight: 600 }}> · {indice + 1} de {lista.length}</span>}
        </div>
        {lista.length > 1 && (
          <>
            <IconButton size="small" style={{ color: "#fff" }} disabled={indice === 0} onClick={() => setIndice(i => i - 1)}><ChevronLeftRoundedIcon /></IconButton>
            <IconButton size="small" style={{ color: "#fff" }} disabled={indice >= lista.length - 1} onClick={() => setIndice(i => i + 1)}><ChevronRightRoundedIcon /></IconButton>
          </>
        )}
        <Tooltip title="Minimizar (continua aqui até marcar)">
          <IconButton size="small" style={{ color: "#fff" }} onClick={() => setMinimizado(true)}><RemoveRoundedIcon /></IconButton>
        </Tooltip>
      </div>

      <div className={classes.corpo}>
        <div>
          <div className={classes.nome}>{atual.candidateName || "Candidato"}</div>
          <div className={classes.linha}>
            {diaCurto(atual.startAt)} · {hora(atual.startAt)}–{hora(atual.endAt)}{atual.jobTitle ? ` · ${atual.jobTitle}` : ""}
          </div>
          <span className={classes.tempo} style={{ color: corTempo, background: fade(corTempo, .1), marginTop: 8 }}>{textoTempo}</span>
        </div>

        {modo === "aviso" ? (
          <>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>O candidato veio na entrevista?</div>
            <div className={classes.botoes}>
              <Button className={`${classes.botao} ${classes.veio}`} disabled={salvando} onClick={() => marcar("compareceu")}>✅ Compareceu</Button>
              <Button className={`${classes.botao} ${classes.faltou}`} disabled={salvando} onClick={() => marcar("faltou")}>❌ Faltou</Button>
              <Button className={`${classes.botao} ${classes.reagendar}`} disabled={salvando} onClick={abrirReagendar}>🔁 Reagendar</Button>
            </div>
            {atual.ticketId && (
              <Button color="primary" className={classes.link} onClick={() => history.push(`/atendimentos/${atual.ticketId}`)}>
                Abrir conversa →
              </Button>
            )}
          </>
        ) : (
          <>
            {livres === null ? (
              <div style={{ textAlign: "center", padding: 12 }}><CircularProgress size={22} /></div>
            ) : !livres.length ? (
              <div className={classes.vazio}>Não há horários livres na agenda. Abra horários na Agenda e tente de novo.</div>
            ) : (
              <>
                <div className={classes.dias}>
                  {[...porDia.keys()].map(k => (
                    <button key={k} type="button" className={`${classes.chip} ${dia === k ? classes.chipAtivo : ""}`}
                      onClick={() => { setDia(k); setNovoId(null); }}>
                      {diaCurto(porDia.get(k)[0].startAt)}
                    </button>
                  ))}
                </div>
                <div className={classes.horas}>
                  {doDia.map(s => (
                    <button key={s.id} type="button" className={`${classes.chip} ${novoId === s.id ? classes.chipAtivo : ""}`}
                      onClick={() => setNovoId(s.id)}>
                      {hora(s.startAt)}
                    </button>
                  ))}
                </div>
                <FormControlLabel
                  control={<Checkbox color="primary" checked={avisar} onChange={e => setAvisar(e.target.checked)} size="small" />}
                  label={<span style={{ fontSize: 13 }}>Avisar o candidato no WhatsApp</span>}
                />
              </>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <Button className={classes.botao} startIcon={<ArrowBackRoundedIcon />} onClick={() => setModo("aviso")} disabled={salvando}>Voltar</Button>
              <Button className={classes.botao} variant="contained" color="primary" disableElevation style={{ flex: 1 }}
                disabled={salvando || !novoId} onClick={reagendar}>
                {salvando ? <CircularProgress size={18} color="inherit" /> : "Confirmar novo horário"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AvisoEntrevista;
